/**
 * /callbacks — Callbacks Admin list (Story 7.13 + 7.13b refactor).
 *
 * Lista paginada com filtros laterais (status multi, campanha UUID, telefone
 * substring, range de scheduled_at). Row click → /callbacks/:id. Phone is
 * always rendered masked — the API enforces masking server-side.
 *
 * Refactor 7.13b extracted:
 *   - filter UI    → `CallbackFilters`
 *   - typed client → `lib/callbacks.ts`
 *   - list query   → `useCallbacksList`
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CallbackFilters } from '@/components/callbacks/CallbackFilters';
import { CallbackStatusBadge } from '@/components/callbacks/CallbackStatusBadge';
import { useCallbacksList } from '@/hooks/useCallbacks';
import {
  EMPTY_CALLBACK_FILTERS,
  toCallbackQueryString,
  type CallbackListFilters,
} from '@/lib/callbacks';
import { ApiError } from '@/lib/api';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';

const DEFAULT_PAGE_SIZE = 50;

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatDateTime(iso: string | null): string {
  return fmtDateTime(iso) || '—';
}

export function CallbacksPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CallbackListFilters>(EMPTY_CALLBACK_FILTERS);
  const [page, setPage] = useState(1);

  const qs = toCallbackQueryString(filters, page, DEFAULT_PAGE_SIZE);
  const listQuery = useCallbacksList(qs);

  const totalPages = listQuery.data
    ? Math.max(1, Math.ceil(listQuery.data.total / listQuery.data.pageSize))
    : 1;

  function handleFiltersChange(next: CallbackListFilters) {
    setFilters(next);
    setPage(1);
  }

  function handleClear() {
    setFilters(EMPTY_CALLBACK_FILTERS);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Callbacks</h1>
        <p className="text-sm text-muted-foreground">
          Visualize callbacks agendados e execute ações (cancelar, reexecutar).
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <CallbackFilters value={filters} onChange={handleFiltersChange} onClear={handleClear} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div data-testid="callbacks-skeleton" className="p-6">
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-muted" />
                ))}
              </div>
            </div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar callbacks.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Agendado para</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Tentativas</th>
                    <th className="px-4 py-2">Telefone</th>
                    <th className="px-4 py-2">Campanha</th>
                    <th className="px-4 py-2">Último erro</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.items.length > 0 ? (
                    listQuery.data.items.map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`callback-row-${c.id}`}
                        className="cursor-pointer border-b hover:bg-accent/40 last:border-b-0"
                        onClick={() => navigate(`/callbacks/${c.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/callbacks/${c.id}`);
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Abrir callback ${c.id}`}
                      >
                        <td className="px-4 py-2">{formatDateTime(c.scheduledAt)}</td>
                        <td className="px-4 py-2">
                          <CallbackStatusBadge status={c.executionStatus} />
                        </td>
                        <td className="px-4 py-2">{c.attemptCount}</td>
                        <td className="px-4 py-2 font-mono text-xs">{c.maskedPhone}</td>
                        <td className="px-4 py-2">{c.campaignName}</td>
                        <td className="max-w-[12rem] truncate px-4 py-2 text-xs text-muted-foreground sm:max-w-[18rem]">
                          {c.lastError ?? '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum callback encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {listQuery.data && listQuery.data.total > 0 ? (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="text-muted-foreground">
            Página {listQuery.data.page} de {totalPages} ({listQuery.data.total} total)
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </div>
  );
}
